"use client";

import { useEffect, useState } from "react";
import { COPILOT_CONVERSATION_PROXY_ROUTE } from "@/utils/constants";
import { resolveStoredConversationId } from "@/utils/copilotConversationId";

function getConversationIdFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const id = new URLSearchParams(window.location.search).get("conversationId");
  if (!id || id === "undefined" || id === "null" || !id.trim()) return null;
  return id;
}

function setConversationIdInUrl(id: string): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (url.searchParams.get("conversationId") === id) return;
  url.searchParams.set("conversationId", id);
  window.history.replaceState({}, "", url.toString());
}

function generateUUID(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
    (
      Number(c) ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(c) / 4)))
    ).toString(16),
  );
}

interface UseLoadOrCreateCopilotConversationIdOptions {
  /** True once the user is confirmed logged in. */
  isLoggedIn: boolean;
  /**
   * Pass true while auth state is still being loaded.
   * The hook will hold off fetching from the backend until this is false.
   */
  skip: boolean;
  /** Scopes the stored conversation to a specific copilot surface. Defaults to "support". */
  copilotId?: string;
}

interface UseLoadOrCreateCopilotConversationIdResult {
  /** null while loading; a UUID string once resolved. */
  conversationId: string | null;
  isLoading: boolean;
}

/**
 * Resolves the copilot conversationId to seed CopilotProvider's
 * `initialConversationId` before the conversation is loaded.
 *
 * Priority:
 * 1. `?conversationId=` URL param — resolved synchronously after mount.
 * 2. Stored ID from the backend (GET /copilot-conversation) — for logged-in
 *    users when no URL param is present. The URL is updated via replaceState
 *    so that CopilotProvider can load the conversation history.
 * 3. Fresh UUID — fallback when no stored ID exists or the user is logged out.
 *
 * `isLoading` is true until one of the above resolves.
 * Pass `skip: true` while auth is still in-flight to defer the backend fetch.
 */
export function useLoadOrCreateCopilotConversationId({
  isLoggedIn,
  skip,
  copilotId,
}: UseLoadOrCreateCopilotConversationIdOptions): UseLoadOrCreateCopilotConversationIdResult {
  const [state, setState] =
    useState<UseLoadOrCreateCopilotConversationIdResult>({
      conversationId: null,
      isLoading: true,
    });

  useEffect(() => {
    // Each effect invocation gets its own `cancelled` flag. The cleanup
    // function sets it to true when the effect is torn down (including
    // React StrictMode's extra mount/unmount cycle in development), which
    // prevents a stale async response from updating state after the effect
    // has been superseded.
    let cancelled = false;

    // Always check the URL first. This handles both the common "navigate with
    // ?conversationId=" case and the SSR→client transition where the initial
    // state was null (no window during server render).
    //
    // IMPORTANT: this must only run after CopilotProvider has been prevented
    // from mounting (by gating on conversationId !== null in the parent).
    // CopilotProvider writes its own random UUID to the URL via replaceState
    // on mount, which would pollute this read if it ran first.
    const urlId = getConversationIdFromUrl();
    if (urlId) {
      setState({ conversationId: urlId, isLoading: false });
      return () => {
        cancelled = true;
      };
    }

    // Hold off until auth state has settled (e.g. while useCurrentUserContext
    // is still fetching).
    if (skip) {
      return () => {
        cancelled = true;
      };
    }

    const resolve = (id: string) => {
      if (cancelled) return;
      setConversationIdInUrl(id);
      setState({ conversationId: id, isLoading: false });
    };

    if (!isLoggedIn) {
      // No session — can't look up a stored conversation id. Use a fresh UUID.
      resolve(generateUUID());
      return () => {
        cancelled = true;
      };
    }

    // Fetch the stored conversationId for this user from the backend.
    const url = copilotId
      ? `${COPILOT_CONVERSATION_PROXY_ROUTE}?copilot_id=${encodeURIComponent(copilotId)}`
      : COPILOT_CONVERSATION_PROXY_ROUTE;
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        // Treat conversations idle for more than a day as expired —
        // resuming them would drop the user into stale context.
        const stored = resolveStoredConversationId(data);
        resolve(stored ?? generateUUID());
      })
      .catch(() => {
        // Network/auth error — fall back to a fresh UUID rather than blocking.
        resolve(generateUUID());
      });

    return () => {
      cancelled = true;
    };
  }, [skip, isLoggedIn, copilotId]);

  return state;
}
